import { useState } from "react";
import { api } from "@/lib/api";
import { AI_MODELS } from "@/lib/format";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sparkle, PaperPlaneRight, Lightning } from "@phosphor-icons/react";

const SUGGESTIONS = [
  "Which telecaller has the best connect rate?",
  "Show me all BD partnerships in discussion stage",
  "Which marketing channel has the best conversion?",
  "Which batches are unlikely to fill before start date?",
];

export default function AIPanel({ open, onOpenChange }) {
  const [model, setModel] = useState("gpt-5.4");
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  const ask = async (q) => {
    const query = q || question;
    if (!query.trim() || loading) return;
    setMessages((m) => [...m, { role: "user", text: query }]);
    setQuestion("");
    setLoading(true);
    try {
      const res = await api.post("/ai/query", { question: query, model });
      setMessages((m) => [...m, { role: "ai", text: res.data.answer }]);
    } catch {
      setMessages((m) => [...m, { role: "ai", text: "Sorry, I couldn't process that right now." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md p-0 flex flex-col bg-navy text-white border-l-0" data-testid="ai-panel">
        <SheetHeader className="px-5 py-4 border-b border-white/10">
          <SheetTitle className="text-white font-heading flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-gold flex items-center justify-center">
              <Sparkle size={18} weight="fill" className="text-navy" />
            </div>
            Institute Intelligence
          </SheetTitle>
        </SheetHeader>

        <div className="px-5 py-3 border-b border-white/10">
          <Select value={model} onValueChange={setModel}>
            <SelectTrigger data-testid="ai-model-select" className="bg-white/5 border-white/10 text-white text-xs h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AI_MODELS.map((m) => (
                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {messages.length === 0 && (
            <div className="space-y-2">
              <p className="text-slate-400 text-xs uppercase tracking-widest mb-3">Try asking</p>
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => ask(s)}
                  className="w-full text-left text-sm bg-white/5 hover:bg-white/10 rounded-lg px-3 py-2.5 flex items-center gap-2 transition-transform duration-200 hover:translate-x-1"
                >
                  <Lightning size={15} weight="fill" className="text-gold shrink-0" />
                  {s}
                </button>
              ))}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
              <div className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${
                m.role === "user" ? "bg-gold text-navy font-medium" : "bg-white/8 text-slate-100"
              }`}>
                {m.text}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-white/8 rounded-xl px-4 py-3 flex gap-1.5">
                <span className="h-2 w-2 rounded-full bg-gold animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="h-2 w-2 rounded-full bg-gold animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="h-2 w-2 rounded-full bg-gold animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-2 bg-white/8 rounded-xl px-3 py-2">
            <input
              data-testid="ai-query-input"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && ask()}
              placeholder="Ask about your institute data…"
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400 text-white"
            />
            <button
              data-testid="ai-send-button"
              onClick={() => ask()}
              disabled={loading}
              className="h-8 w-8 rounded-lg bg-gold flex items-center justify-center text-navy disabled:opacity-50"
            >
              <PaperPlaneRight size={16} weight="fill" />
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
